import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { resourcesService, Resource } from '@/services/resources';
import { Button } from '@/components/ui/Button';
import { 
  LinkIcon,
  DocumentIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

// =====================================================
// RESOURCES WIDGET COMPONENT
// TMOS Platform - Smart Sidebar Resources Widget
// =====================================================
// Component: ResourcesWidget
// Description: Smart sidebar widget with featured + recent resources
// Author: TMOS Development Team
// Date: January 30, 2025
// =====================================================

interface ResourcesWidgetProps {
  isCollapsed?: boolean;
  maxItems?: number;
  mode?: 'recent' | 'featured' | 'smart';
}

export const ResourcesWidget: React.FC<ResourcesWidgetProps> = ({
  isCollapsed = false,
  maxItems = 5,
  mode = 'smart'
}) => {
  const navigate = useNavigate();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // =====================================================
  // DATA LOADING
  // =====================================================

  useEffect(() => {
    loadResources();
  }, [mode, maxItems]);

  const loadResources = async () => {
    try {
      setLoading(true);
      const resources = await resourcesService.getWidgetResources(mode, maxItems);
      setResources(resources);
      setCurrentIndex(0); // Reset to first item
    } catch (err) {
      console.error('Error loading widget resources:', err);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // SCROLLING LOGIC
  // =====================================================

  useEffect(() => {
    if (resources.length <= 1 || isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Start auto-scrolling
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % resources.length);
    }, 3000); // Change every 3 seconds

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [resources.length, isPaused]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // =====================================================
  // RESOURCE ACTIONS
  // =====================================================

  const handleResourceClick = async (resource: Resource) => {
    try {
      // Log access
      await resourcesService.logAccess(resource.id, 'view');
      
      // Navigate to resources page with the specific resource
      navigate(`/resources?highlight=${resource.id}`);
    } catch (err) {
      console.error('Error accessing resource:', err);
      // Still navigate even if logging fails
      navigate(`/resources?highlight=${resource.id}`);
    }
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  // =====================================================
  // RENDER HELPERS
  // =====================================================

  const getResourceIcon = (resource: Resource) => {
    return resource.resource_type === 'link' ? (
      <LinkIcon className="h-4 w-4 text-blue-500" />
    ) : (
      <DocumentIcon className="h-4 w-4 text-green-500" />
    );
  };

  const getCurrentResource = () => {
    if (resources.length === 0) return null;
    return resources[currentIndex];
  };

  // =====================================================
  // RENDER
  // =====================================================

  if (isCollapsed) {
    return null; // Don't show when sidebar is collapsed
  }

  const currentResource = getCurrentResource();

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 shadow-sm"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
        </div>
      ) : currentResource ? (
        <div
          onClick={() => handleResourceClick(currentResource)}
          className="flex items-center space-x-3 p-4 cursor-pointer group transition-all duration-200 hover:bg-gray-50"
        >
          {getResourceIcon(currentResource)}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate group-hover:text-gray-900">
              {currentResource.title}
            </p>
            {currentResource.description && (
              <p className="text-xs text-gray-500 truncate mt-1">
                {currentResource.description}
              </p>
            )}
          </div>
          <ArrowTopRightOnSquareIcon className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      ) : (
        <div className="text-center py-6">
          <DocumentIcon className="mx-auto h-8 w-8 text-gray-300" />
          <p className="text-xs text-gray-400 mt-2">No resources</p>
          <Button
            size="sm"
            className="mt-3 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]"
            onClick={() => navigate('/resources')}
          >
            Add Resource
          </Button>
        </div>
      )}

      {/* Progress indicators */}
      {resources.length > 1 && (
        <div className="flex justify-center space-x-1 pb-3">
          {resources.map((_, index) => (
            <div
              key={index}
              className={`h-1 w-1 rounded-full transition-colors duration-300 ${
                index === currentIndex ? 'bg-blue-500' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
